import React, { useEffect, useState } from 'react';
import { useNode, useEditor } from "@craftjs/core";
import {
  TextField,
  Box,
  Paper,
  Checkbox,
  FormControlLabel,
  FormControl,
  FormLabel
} from '@mui/material';
import { useQwanta } from 'qwanyx';

export const BasicRecall = ({
  name = "BasicRecall",
  qwantumId = "basicRecall",
  title,
  question,
  correctAnswer,
  useEmptyField,
  hidden = false,
  style,
  ...props
}) => {
  const { 
    connectors: { connect, drag },
    isSelected,
    isHovered,
    actions: { setProp }
  } = useNode((state) => ({
    isSelected: state.events.selected,
    isHovered: state.events.hovered,
  }));

  const { enabled } = useEditor((state) => ({
    enabled: state.options.enabled
  }));

  const [savedState, setSavedState] = useQwanta(qwantumId);
  const [prevData, setPrevData] = useState("");
  
  // Local state for current editing values
  const [currentData, setCurrentData] = useState({
    title: title || "",
    question: question || "",
    correctAnswer: correctAnswer || "",
    useEmptyField: useEmptyField || false
  });

  // Load saved state on mount and when savedState changes
  useEffect(() => {
    if (savedState) {
      try {
        const parsed = typeof savedState === 'string' ? JSON.parse(savedState) : savedState;
        const newData = {
          title: parsed.title || "",
          question: parsed.question || "",
          correctAnswer: parsed.correctAnswer || "",
          useEmptyField: parsed.useEmptyField || false
        };
        setCurrentData(newData);
        // Only update CraftJS props when loading from saved state
        setProp(props => {
          props.title = newData.title;
          props.question = newData.question;
          props.correctAnswer = newData.correctAnswer;
          props.useEmptyField = newData.useEmptyField;
        });
      } catch (error) {
        console.error('Failed to load BasicRecall saved state:', error);
      }
    } else {
      // Initialize with current props if no saved state
      setCurrentData({ 
        title: title || "", 
        question: question || "", 
        correctAnswer: correctAnswer || "", 
        useEmptyField: useEmptyField || false 
      });
    }
  }, [savedState]); // Remove setProp from dependencies

  if (hidden) return null;

  const persistData = (data) => {
    const questionData = {
      type: "basic",
      qId: qwantumId,
      ...data
    };
    
    const dataString = JSON.stringify(questionData);
    if (dataString !== prevData) {
      setSavedState(dataString);
      setPrevData(dataString);
    }
  };

  const handleChange = (field, value) => {
    // Only update local state during editing
    setCurrentData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleBlur = (field, value) => {
    // Update local state and persist, but DON'T update CraftJS props during editing
    const updatedData = {
      ...currentData,
      [field]: value
    };
    setCurrentData(updatedData);
    persistData(updatedData);
  };

  const handleCheckboxChange = (field, value) => {
    // Update local state and persist, but DON'T update CraftJS props during editing
    const updatedData = {
      ...currentData,
      [field]: value
    };
    setCurrentData(updatedData);
    persistData(updatedData);
  };

  // Selection styling - only show when editor is enabled
  const getSelectionStyle = () => {
    if (!enabled) return {};
    
    if (isSelected) {
      return {
        outline: '2px solid #2196f3',
        outlineOffset: '2px'
      };
    }
    if (isHovered) {
      return {
        outline: '1px dashed #ff9800',
        outlineOffset: '1px'
      };
    }
    return {};
  };

  return (
    <div 
      ref={ref => enabled ? connect(drag(ref)) : connect(ref)}
      name={name}
      style={{
        transition: 'all 0.2s ease',
        position: 'relative',
        width: '100%',
        ...style,
        ...getSelectionStyle()
      }}
      {...props}
    >
      <Paper elevation={3} sx={{ padding: 2, width: '100%', maxWidth: 700, margin: 'auto' }}>
        <TextField
          fullWidth
          value={currentData.title}
          onChange={(e) => handleChange('title', e.target.value)}
          onBlur={(e) => handleBlur('title', e.target.value)}
          variant="outlined"
          sx={{ marginTop: 1 }}
          disabled={!enabled}
          placeholder="Enter question title"
        />

        <TextField
          multiline
          rows={3}
          fullWidth
          value={currentData.question}
          onChange={(e) => handleChange('question', e.target.value)}
          onBlur={(e) => handleBlur('question', e.target.value)}
          sx={{ marginTop: 2 }}
          disabled={!enabled}
          placeholder="Enter the question"
        />

        <Box sx={{ marginTop: 2 }}>
          <TextField
            fullWidth
            value={currentData.correctAnswer}
            onChange={(e) => handleChange('correctAnswer', e.target.value)}
            onBlur={(e) => handleBlur('correctAnswer', e.target.value)}
            sx={{ marginTop: 1 }}
            disabled={!enabled}
            placeholder="Enter the correct answer"
          />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 1 }}>
          <FormControlLabel
            control={<Checkbox
              checked={currentData.useEmptyField}
              onChange={(e) => handleCheckboxChange('useEmptyField', e.target.checked)}
              disabled={!enabled}
            />}
            label="Use an empty field"
          />
        </Box>
      </Paper>
    </div>
  );
};

const BasicRecallSettings = () => {
  const { 
    actions: { setProp }, 
    name, qwantumId, title, question, correctAnswer, useEmptyField
  } = useNode((node) => ({
    name: node.data.props.name,
    qwantumId: node.data.props.qwantumId,
    title: node.data.props.title,
    question: node.data.props.question,
    correctAnswer: node.data.props.correctAnswer,
    useEmptyField: node.data.props.useEmptyField
  }));

  return (
    <div>
      <FormControl fullWidth margin="normal">
        <FormLabel>Component Name</FormLabel>
        <TextField
          value={name || ''}
          onChange={(e) => setProp(props => props.name = e.target.value)}
          size="small"
          placeholder="Enter component name"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Quantum ID</FormLabel>
        <TextField
          value={qwantumId || ''}
          onChange={(e) => setProp(props => props.qwantumId = e.target.value)}
          size="small"
          placeholder="Enter quantum ID for persistence"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Title (Read-only)</FormLabel>
        <TextField
          value={title || ''}
          size="small"
          disabled
          placeholder="Title will appear here"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Question (Read-only)</FormLabel>
        <TextField
          value={question || ''}
          size="small"
          multiline
          rows={2}
          disabled
          placeholder="Question will appear here"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Correct Answer (Read-only)</FormLabel>
        <TextField
          value={correctAnswer || ''}
          size="small"
          disabled
          placeholder="Answer will appear here"
        />
      </FormControl>

      <FormControlLabel
        control={
          <Checkbox
            checked={useEmptyField}
            disabled
          />
        }
        label="Use empty field (Read-only)"
      />
    </div>
  );
};

BasicRecall.craft = {
  props: {
    name: "BasicRecall",
    qwantumId: "basicRecall"
  },
  related: {
    settings: BasicRecallSettings
  }
};